import { CompositeLayer, PickingInfo, CompositeLayerProps, Layer } from "@deck.gl/core"
import FlightPathsLayer from "./VideoPathsLayer.class"
import { DroneIconColors, VideoDroneLayers } from "./VideoDroneLayer"
import VideoPointsLayer from "./VideoPointsLayer"
import { calculateCoordFromPointWithDistance } from "@nova/calculate-geospatial"
import VideoProjectionLayer, {
    getVideoFrameBbox,
    VideoFrameBoundingBox,
} from "./VideoProjectionLayer"
import VideoCrosshairLayer from "./VideoCrosshairLayer"
import { VideoMetadata } from "@nova/slice-video"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import store from "integrations/redux/store"
import { selectLiveVideoFlightById } from "features/entity-caching/flights/selectors"

type _VideoLayerProps = {
    flightId?: string
    distance?: number
    frameEnabled?: boolean
    crosshairEnabled?: boolean
    loading?: boolean
    mediaType?: "image" | "video"
    frameNumber?: number
    droneEnabled?: boolean
    flightPathEnabled?: boolean
    live?: boolean
    color?: DroneIconColors
    getVideoElement?: () => HTMLVideoElement | null
}

export type VideoLayerProps = CompositeLayerProps & _VideoLayerProps
export default class VideoLayer<ExtraPropsType = VideoLayerProps> extends CompositeLayer<
    ExtraPropsType & Required<_VideoLayerProps>
> {
    flightId = ""
    state = {} as {
        frames: VideoMetadata
        frameNumber: number
        loading: boolean
        flightId?: string
        dronePosition?: VideoMetadata[number]
        triangle?: [number, number, number][]
        projection?: VideoFrameBoundingBox
        yaw?: number
        isMMA?: boolean
    }
    updateState({
        // props,
        changeFlags,
    }: {
        props: VideoLayerProps
        changeFlags: Record<string, unknown>
    }) {
        if (changeFlags.dataChanged) {
            const { data, flightId, mediaType } = this.props
            const frames = data as VideoMetadata
            if (frames.length === 0) {
                this.flightId = ""
                return this.setState({
                    loading: true,
                    flightId: "",
                })
            } else {
                if (mediaType === "video") {
                    if (this.flightId !== flightId) {
                        this.setState({
                            loading: true,
                            flightId,
                        })
                        // todo connor: this won't work for video colorfinder I don't think at the moment
                        this.setDronePosition(this.props.frameNumber)
                    }
                } else {
                    this.setDronePosition(this.props.frameNumber - 1)
                }
            }
            const state = store.getState()
            const liveVideoFlight = selectLiveVideoFlightById(flightId)(state)
            const isMMA = liveVideoFlight?.isMMA
            if (isMMA) this.setState({ isMMA })
        } else if (this.state.frameNumber !== this.props.frameNumber) {
            this.setDronePosition(this.props.frameNumber - 1)
        }
    }

    onClick = ({ index }: PickingInfo) => {
        const video = this.props.getVideoElement()
        if (!video) return false
        const ratio = index / this.state.frames.length
        video.currentTime = Math.round(video.duration * ratio)
        this.setDronePosition(
            Math.round((video.currentTime / video.duration) * this.state.frames.length)
        )
        return false
    }

    setDronePosition = (frame: number) => {
        const frames = this.props.data as VideoMetadata
        if (!frames || frames.length === 0 || frame < 0 || frame >= frames.length || !frames[frame])
            return
        const centerPoint = frames[frame].slice(0, 2) as [number, number]
        const yaw = frames[frame][4]
        const triangle = [
            [
                ...calculateCoordFromPointWithDistance(centerPoint, yaw, 22.91),
                (frames[frame + 120] || frames[frame])[2] + 3,
            ],
            [
                ...calculateCoordFromPointWithDistance(centerPoint, yaw + 35, 14),
                (frames[frame + 45] || frames[frame])[2] + 3,
            ],
            [
                ...calculateCoordFromPointWithDistance(centerPoint, yaw - 35, 14),
                (frames[frame + 45] || frames[frame])[2] + 3,
            ],
        ]
        const setProjection = () => {
            if (
                (this.props.mediaType === "image" && this.state.frameNumber - 1 === frame) ||
                this.props.mediaType === "video"
            ) {
                this.setState({
                    projection: getVideoFrameBbox(frames[frame]),
                })
            }
        }

        this.setState({
            dronePosition: frames[frame],
            triangle: triangle,
            projection: !this.props.live
                ? getVideoFrameBbox(frames[frame], setProjection)
                : undefined,
            yaw: frames[frame][4],
            frameNumber: this.props.frameNumber,
            frames,
            loading: false,
        })
    }

    renderLayers() {
        if (this.props.loading || !this.state.frames || !this.props.flightId) return []
        const layers: Layer[] = []
        if (this.props.frameEnabled) {
            layers.push(
                new VideoProjectionLayer({
                    id: "video-projection-layer" + this.props.flightId,
                    data: this.state.projection?.[0],
                    visible: !!this.state.frames?.length,
                    extensions: [...getGlobalTerrainData().extensions],
                })
            )
        }
        if (this.props.crosshairEnabled) {
            layers.push(
                new VideoCrosshairLayer({
                    id: "video-crosshair-layer" + this.props.flightId,
                    data: this.state.projection
                        ? [[...this.state.projection?.[1], this.state.yaw]]
                        : [],
                    visible: !!this.state.frames?.length,
                    extensions: [...getGlobalTerrainData().extensions],
                })
            )
        }
        if (this.props.flightPathEnabled !== false) {
            const frames = this.state.frames || []
            const validPathPoints = frames.filter(
                (point) => Math.abs(point[0]) > 1 && Math.abs(point[1]) > 1
            )
            layers.push(
                new FlightPathsLayer({
                    id: "video-paths" + this.props.flightId,
                    data: validPathPoints.length
                        ? [
                              {
                                  path: validPathPoints,
                              },
                          ]
                        : [],
                    extensions: [...getGlobalTerrainData().extensions],
                    terrainDrawMode: "offset",
                    parameters: { depthCompare: "always" },
                    visible: (this.state?.frames?.length || 0) > 0,
                })
            )
        }
        if (!this.props.live && this.state.frames) {
            layers.push(
                new VideoPointsLayer({
                    id: "video-points" + this.props.flightId,
                    data: this.state.frames,
                    visible: !!this.state.frames?.length,
                })
            )
        }
        if (this.props.droneEnabled !== false) {
            layers.push(
                ...VideoDroneLayers({
                    id: "video-drone" + this.props.flightId,
                    data: [this.state.dronePosition],
                    isMMA: this.state.isMMA || false,
                    extensions: [...getGlobalTerrainData().extensions],
                    color: this.props.color,
                })
            )
        }

        return layers
    }
}
